import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { getAppliedParamsCount } from '~/components/Filters/FiltersBar/filtersBarUtils';
import { FilterTrigger } from '~/components/Filters/SingleFilters/FilterTrigger';
import { type SingleFilterProps } from '~/components/Filters/types';
import { SingleUploaderFilter } from '~/components/Filters/UploaderFilter/SingleUploaderFilter';
import { baseFiltersOptions } from '~/components/FiltersMenu/types';
import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { removeFilterAction } from '~/store/filters/actions';
import { type Filters, type SelectedFilter } from '~/store/filters/types';

export type UploaderFilterTriggerProps = {
  uploaders: Filters['uploaders'];
} & SingleFilterProps;

export const UploaderFilterTrigger = memo(
  ({ onApplyClick, uploaders, onRemoveClick, onOpenChange }: UploaderFilterTriggerProps) => {
    const dispatch = useDispatch();
    const { uploadersParam } = useFilterParams();

    const content = useMemo(
      () => <SingleUploaderFilter uploaders={uploaders} onApplyClick={onApplyClick} />,
      [onApplyClick, uploaders],
    );

    const appliedUploadersCount = getAppliedParamsCount(uploadersParam);

    const removeFilter = useCallback(async () => {
      const filter: SelectedFilter = { type: 'uploader' };
      dispatch(removeFilterAction({ filter: { type: 'uploader' } }));
      onRemoveClick?.([filter]);
    }, [dispatch, onRemoveClick]);

    return (
      <FilterTrigger
        onRemoveClick={removeFilter}
        count={appliedUploadersCount}
        filterContent={content}
        title={baseFiltersOptions.uploader.label}
        Icon={baseFiltersOptions.uploader.icon}
        onOpenChange={onOpenChange}
      />
    );
  },
);

UploaderFilterTrigger.displayName = 'UploaderFilterTrigger';
